import type { ContainerSpec } from "@ide/shared/desktop";

/**
 * Como rodar o projeto num contêiner, a partir dos arquivos: Compose > Dockerfile > runtime
 * detectado (Node, Python, Go, PHP, Java, site estático). A pessoa confere e ajusta antes de rodar.
 */
export interface RunPlan {
  id: "compose" | "dockerfile" | "image";
  title: string;
  summary: string;
  spec: ContainerSpec;
}

export const COMPOSE_FILES = ["compose.yaml", "compose.yml", "docker-compose.yml", "docker-compose.yaml"];

interface Pkg {
  scripts?: Record<string, string>;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  main?: string;
}

/** Ferramentas que só servem arquivos estáticos (o Live Server faz o mesmo, recarregando). */
const STATIC_SERVERS = /^(live-server|lite-server|http-server|serve|browser-sync|five-server)$/;
/** Dependências de app Node de verdade (servidor ou build): aí não é "site estático". */
const NODE_APP =
  /^(express|fastify|koa|@hapi\/hapi|hapi|@nestjs\/core|next|nuxt|vite|react-scripts|@angular\/core|svelte|@sveltejs\/kit|astro|parcel|webpack|socket\.io|ws|electron|ts-node|tsx|nodemon)$/;

function readPkg(files: Record<string, string>): Pkg | null {
  if (files["package.json"] === undefined) return null;
  try {
    return JSON.parse(files["package.json"] || "{}") as Pkg;
  } catch {
    return {};
  }
}

/**
 * Site estático (HTML, CSS e JS de página): tem index.html e não é um app Node — sem
 * package.json, ou com um package.json só de ferramentas (prettier, live-server, tailwind…). Esses
 * rodam no Live Server, não no `node`.
 */
export function isStaticSite(files: Record<string, string>): boolean {
  const paths = Object.keys(files);
  const html = paths.some((p) => /^(?:(?:public|dist|site|www|docs)\/)?index\.html?$/.test(p));
  if (!html) return false;
  const pkg = readPkg(files);
  if (!pkg) {
    // Sem package.json: um servidor solto (server.js com listen) ainda é app Node.
    return !paths.some(
      (p) =>
        /^(server|app|index)\.(m?js|cjs|ts)$/.test(p) && /\.listen\(|createServer\(/.test(files[p] ?? ""),
    );
  }
  const deps = Object.keys({ ...pkg.dependencies, ...pkg.devDependencies });
  if (deps.some((d) => NODE_APP.test(d))) return false;
  const scripts = Object.values(pkg.scripts ?? {}).join(" ");
  // Scripts que só servem a pasta (live-server, serve…) contam como site; outros (node server.js) não.
  const runs = ["dev", "start", "serve"].map((k) => pkg.scripts?.[k]).filter(Boolean) as string[];
  if (runs.some((r) => !r.split(/\s+/).some((w) => STATIC_SERVERS.test(w)))) return false;
  if (pkg.main && files[pkg.main] !== undefined && /\.listen\(|createServer\(/.test(files[pkg.main] ?? ""))
    return false;
  return !/\bnode\s+\S+\.(m?js|cjs)\b/.test(scripts);
}

/** Portas publicadas no Compose ("8080:80", "127.0.0.1:8080:80", published: 8080). */
export function composePorts(text: string): number[] {
  const out = new Set<number>();
  for (const m of text.matchAll(/^\s*-\s*["']?(?:[\d.]+:)?(\d{1,5}):\d{1,5}(?:\/(?:tcp|udp))?["']?\s*$/gm))
    out.add(Number(m[1]));
  for (const m of text.matchAll(/^\s*published:\s*["']?(\d{1,5})["']?\s*$/gm)) out.add(Number(m[1]));
  return [...out].filter((p) => p > 0 && p < 65536).slice(0, 20);
}

/** Primeira porta do EXPOSE. */
export function exposedPort(dockerfile: string): number | null {
  const m = dockerfile.match(/^\s*EXPOSE\s+(\d{1,5})/im);
  const port = m ? Number(m[1]) : null;
  return port && port < 65536 ? port : null;
}

/** Porta que o código escuta (process.env.PORT || 3000, app.listen(8080)…). */
function codePort(files: Record<string, string>): number | null {
  let seen = 0;
  for (const [path, text] of Object.entries(files)) {
    if (!/\.(m?[jt]s|cjs)$/.test(path) || /(^|\/)(node_modules|dist|build)\//.test(path)) continue;
    if (++seen > 60) break;
    const m =
      text.match(/process\.env\.PORT\s*(?:\|\||\?\?)\s*["']?(\d{2,5})/) ??
      text.match(/\.listen\(\s*(\d{2,5})\s*[,)]/);
    if (m) return Number(m[1]);
  }
  return null;
}

function nodePlan(files: Record<string, string>): RunPlan | null {
  if (files["package.json"] === undefined || isStaticSite(files)) return null;
  let pkg: {
    scripts?: Record<string, string>;
    dependencies?: object;
    devDependencies?: object;
    main?: string;
  } = {};
  try {
    pkg = JSON.parse(files["package.json"]) as typeof pkg;
  } catch {
    /* package.json quebrado: segue com o padrão */
  }
  const deps = { ...pkg.dependencies, ...pkg.devDependencies } as Record<string, string>;
  const scripts = pkg.scripts ?? {};
  const pm = "pnpm-lock.yaml" in files ? "pnpm" : "yarn.lock" in files ? "yarn" : "npm";
  const install = pm === "npm" ? "npm install" : `corepack enable && ${pm} install`;
  // npm repassa argumentos depois de "--"; pnpm e yarn repassam direto.
  const run = (script: string, args = "") =>
    `${pm} run ${script}${args ? (pm === "npm" ? ` -- ${args}` : ` ${args}`) : ""}`;
  let command: string;
  let port = codePort(files) ?? 3000;
  let what = "Node.js";
  if (deps.vite && scripts.dev) {
    command = `${install} && ${run("dev", "--host 0.0.0.0 --port 5173")}`;
    port = 5173;
    what = "Vite";
  } else if (deps.next && scripts.dev) {
    command = `${install} && ${run("dev", "-H 0.0.0.0 -p 3000")}`;
    port = 3000;
    what = "Next.js";
  } else if (deps["@angular/core"]) {
    command = `${install} && npx ng serve --host 0.0.0.0 --port 4200`;
    port = 4200;
    what = "Angular";
  } else if (scripts.dev) {
    command = `${install} && ${run("dev")}`;
  } else if (scripts.start) {
    command = `${install} && ${pm} start`;
  } else {
    command = `${install} && node ${pkg.main ?? "index.js"}`;
  }
  return {
    id: "image",
    title: `${what} (${pm})`,
    summary: `Imagem node:22-alpine com o projeto montado em /app, porta ${port}.`,
    spec: { mode: "image", image: "node:22-alpine", command, containerPort: port },
  };
}

/** Primeiro .py da raiz cujo código casa com o padrão (o app do Streamlit costuma ser app.py). */
export function entryWith(files: Record<string, string>, re: RegExp): string | null {
  const preferred = ["app.py", "main.py", "streamlit_app.py", "Home.py"];
  const candidates = [
    ...preferred.filter((p) => p in files),
    ...Object.keys(files).filter((p) => /^[^/]+\.py$/.test(p)),
  ];
  return candidates.find((p) => re.test(files[p] ?? "")) ?? null;
}

function pythonPlan(files: Record<string, string>): RunPlan | null {
  const req = files["requirements.txt"];
  const rootPy = Object.keys(files).filter((p) => /^[^/]+\.py$/.test(p));
  if (req === undefined && !("pyproject.toml" in files) && !rootPy.length) return null;
  const install =
    req !== undefined
      ? "pip install --no-cache-dir -r requirements.txt && "
      : "pyproject.toml" in files
        ? "pip install --no-cache-dir . && "
        : "";
  const entry = ["main.py", "app.py", "server.py"].find((p) => p in files) ?? rootPy[0] ?? "main.py";
  const mod = entry.replace(/\.py$/, "");
  const text = `${req ?? ""}\n${files["pyproject.toml"] ?? ""}`.toLowerCase();
  let command = `${install}python ${entry}`;
  let port = 8000;
  let what = "Python";
  if (/streamlit/.test(text) || /^\s*import streamlit|^\s*from streamlit/m.test(files[entry] ?? "")) {
    const app = entryWith(files, /import streamlit|from streamlit/) ?? entry;
    command = `${install}streamlit run ${app} --server.address 0.0.0.0 --server.port 8501 --server.headless true`;
    port = 8501;
    what = "Streamlit";
  } else if ("manage.py" in files) {
    command = `${install}python manage.py runserver 0.0.0.0:8000`;
    what = "Django";
  } else if (/fastapi|uvicorn/.test(text)) {
    command = `${install}uvicorn ${mod}:app --host 0.0.0.0 --port 8000 --reload`;
    what = "FastAPI";
  } else if (/flask/.test(text)) {
    command = `${install}flask --app ${mod} run --host 0.0.0.0 --port 5000 --debug`;
    port = 5000;
    what = "Flask";
  }
  return {
    id: "image",
    title: what,
    summary: `Imagem python:3.13-slim com o projeto montado em /app, porta ${port}.`,
    spec: { mode: "image", image: "python:3.13-slim", command, containerPort: port },
  };
}

function otherPlan(files: Record<string, string>): RunPlan | null {
  if ("go.mod" in files) {
    return {
      id: "image",
      title: "Go",
      summary: "Imagem golang:1.25-alpine, go run . na porta 8080.",
      spec: { mode: "image", image: "golang:1.25-alpine", command: "go run .", containerPort: 8080 },
    };
  }
  if ("composer.json" in files || "index.php" in files) {
    const docroot = "public/index.php" in files ? " -t public" : "";
    return {
      id: "image",
      title: "PHP",
      summary: "Imagem php:8.4-cli com o servidor embutido na porta 8000.",
      spec: {
        mode: "image",
        image: "php:8.4-cli",
        command: `php -S 0.0.0.0:8000${docroot}`,
        containerPort: 8000,
      },
    };
  }
  if ("pom.xml" in files) {
    const spring = /spring-boot/.test(files["pom.xml"] ?? "");
    return {
      id: "image",
      title: spring ? "Spring Boot (Maven)" : "Java (Maven)",
      summary: "Imagem maven:3.9-eclipse-temurin-21, porta 8080.",
      spec: {
        mode: "image",
        image: "maven:3.9-eclipse-temurin-21",
        command: spring ? "mvn -q spring-boot:run" : "mvn -q compile exec:java",
        containerPort: 8080,
      },
    };
  }
  const html =
    "index.html" in files ? "" : ["public/", "dist/", "site/", "www/"].find((d) => `${d}index.html` in files);
  if (html !== undefined) {
    return {
      id: "image",
      title: "Site estático (nginx)",
      summary: "nginx:alpine servindo o site. Para recarregar a cada alteração, use Pré-visualizar.",
      spec: {
        mode: "image",
        image: "nginx:alpine",
        mountAt: "/usr/share/nginx/html",
        containerPort: 80,
        hostPort: 8080,
      },
    };
  }
  return null;
}

/** Opções de execução do projeto, a recomendada primeiro (sempre há a de imagem, editável). */
export function detectPlans(files: Record<string, string>): RunPlan[] {
  const plans: RunPlan[] = [];
  const compose = COMPOSE_FILES.find((f) => f in files);
  if (compose) {
    const ports = composePorts(files[compose]!);
    plans.push({
      id: "compose",
      title: `Compose (${compose})`,
      summary: ports.length
        ? `Sobe todos os serviços; portas ${ports.join(", ")}.`
        : "Sobe todos os serviços do arquivo.",
      spec: { mode: "compose", file: compose, ports },
    });
  }
  const dockerfile =
    "Dockerfile" in files ? "Dockerfile" : Object.keys(files).find((p) => /(^|\/)Dockerfile$/.test(p));
  if (dockerfile) {
    const port = exposedPort(files[dockerfile]!) ?? 3000;
    plans.push({
      id: "dockerfile",
      title: `Dockerfile${dockerfile === "Dockerfile" ? "" : ` (${dockerfile})`}`,
      summary: `Constrói a imagem e roda na porta ${port}.`,
      spec: { mode: "dockerfile", file: dockerfile, containerPort: port },
    });
  }
  plans.push(
    nodePlan(files) ??
      pythonPlan(files) ??
      otherPlan(files) ?? {
        id: "image",
        title: "Imagem",
        summary: "Escolha a imagem e o comando.",
        spec: { mode: "image", image: "alpine:3", command: "ls -la", containerPort: null },
      },
  );
  return plans;
}
